import { useEffect, useState } from "react";
import { ImagePlus, Link2, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../../lib/api";
import { useConfirm } from "../../components/Confirm";
import { achievementImages } from "../AchievementsPage";

const MAX_IMAGES = 10;
const empty = { title: "", description: "", images: [], date_label: "", journal: "", order: 0 };

const readImage = (file) => new Promise((resolve, reject) => {
  if (file.size > 2 * 1024 * 1024) return reject(new Error("حجم الصورة يتجاوز 2MB"));
  const img = new Image(); const url = URL.createObjectURL(file);
  img.onload = () => {
    const max = 1400; const scale = Math.min(1, max / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas"); canvas.width = img.width * scale; canvas.height = img.height * scale;
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url); resolve(canvas.toDataURL("image/jpeg", 0.82));
  };
  img.onerror = reject; img.src = url;
});

const AchievementForm = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(initial ? { ...initial, images: achievementImages(initial) } : empty);
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const addImages = (list) => setForm((f) => {
    const images = [...f.images, ...list].slice(0, MAX_IMAGES);
    if (f.images.length + list.length > MAX_IMAGES) toast.warning(`الحد الأقصى ${MAX_IMAGES} صور`);
    return { ...f, images };
  });
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    const read = [];
    for (const f of files) { try { read.push(await readImage(f)); } catch (err) { toast.error(err.message || "تعذر قراءة الصورة"); } }
    if (read.length) addImages(read);
  };
  const addUrl = () => { const u = url.trim(); if (!u) return; addImages([u]); setUrl(""); };
  const removeImage = (i) => setForm({ ...form, images: form.images.filter((_, k) => k !== i) });
  const moveFirst = (i) => setForm({ ...form, images: [form.images[i], ...form.images.filter((_, k) => k !== i)] });

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    const payload = { title: form.title, description: form.description, images: form.images, image: form.images[0] || "", date_label: form.date_label, journal: form.journal, order: Number(form.order) || 0 };
    try { initial ? await api.put(`/admin/achievements/${initial.id}`, payload) : await api.post("/admin/achievements", payload); toast.success("تم الحفظ"); onSaved(); }
    catch (err) { toast.error(errorMessage(err, "تعذر الحفظ")); } finally { setLoading(false); }
  };

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" data-testid="achievement-form-modal">
        <button className="modal-close" onClick={onClose} data-testid="achievement-form-close"><X size={18} /></button>
        <h2>{initial ? "تعديل الإنجاز" : "إنجاز جديد"}</h2>
        <form className="form" onSubmit={submit}>
          <label className="full">العنوان (يظهر بخط عريض)<input value={form.title} onChange={set("title")} required data-testid="ach-title" /></label>
          <label className="full">التفاصيل<textarea value={form.description} onChange={set("description")} rows={5} placeholder="اكتب تفاصيل الإنجاز بوضوح — تظهر كاملة للزوار" data-testid="ach-description" /></label>
          <label>المجلة / التصنيف<input value={form.journal} onChange={set("journal")} placeholder="مثال: Cureus · Q2 · Scopus" data-testid="ach-journal" /><span className="hint">إذا كتبت شيئاً هنا يظهر للزائر زر «المجلة / التصنيف» يعرض ما كتبته</span></label>
          <label>التاريخ (نص)<input value={form.date_label} onChange={set("date_label")} placeholder="يناير 2026" data-testid="ach-date" /></label>
          <label>الترتيب<input type="number" value={form.order} onChange={set("order")} data-testid="ach-order" /></label>
          <div className="full">
            <span style={{ fontSize: 13, fontWeight: 700, display: "block", marginBottom: 8 }}>الصور ({form.images.length}/{MAX_IMAGES}) — الصورة الأولى هي الغلاف</span>
            {form.images.length > 0 && (
              <div className="img-manager" data-testid="ach-images-list">
                {form.images.map((src, i) => (
                  <div className="img-item" key={i} data-testid={`ach-image-item-${i}`}>
                    <img src={src} alt="" />
                    {i === 0 ? <span className="cover-tag">الغلاف</span> : <button type="button" className="mini-btn" onClick={() => moveFirst(i)} data-testid={`ach-image-cover-${i}`}>اجعلها الغلاف</button>}
                    <button type="button" className="img-remove" onClick={() => removeImage(i)} data-testid={`ach-image-remove-${i}`}><X size={14} /></button>
                  </div>))}
              </div>
            )}
            <label className="upload-box" style={{ display: "block", marginTop: 10 }}>
              <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}><ImagePlus size={16} />إضافة صور من الجهاز (يمكن اختيار عدة صور — حتى 2MB للصورة)</span>
              <input type="file" accept="image/*" multiple onChange={onFiles} style={{ display: "none" }} data-testid="ach-image-file" />
            </label>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="أو الصق رابط صورة https://..." data-testid="ach-image-url" style={{ flex: 1 }} />
              <button type="button" className="btn btn-outline btn-sm" onClick={addUrl} data-testid="ach-image-url-add"><Link2 size={15} />إضافة</button>
            </div>
          </div>
          <button className="btn btn-primary" type="submit" disabled={loading} data-testid="ach-save-button">{loading ? <span className="loader" /> : "حفظ"}</button>
        </form>
      </div>
    </div>
  );
};

export default function AdminAchievements() {
  const confirm = useConfirm();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(undefined);
  const load = () => api.get("/achievements").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (a) => { if (!(await confirm(`حذف الإنجاز "${a.title}" نهائياً؟`, { danger: true, okLabel: "حذف" }))) return; try { await api.delete(`/admin/achievements/${a.id}`); toast.success("تم الحذف"); load(); } catch (err) { toast.error(errorMessage(err, "تعذر الحذف")); } };

  return (
    <div data-testid="admin-achievements">
      <div className="admin-top"><div><span className="kicker">Achievements</span><h1>إنجازات المنصة</h1></div><button className="btn btn-primary btn-sm" onClick={() => setEditing(null)} data-testid="add-achievement-button"><Plus size={16} />إنجاز جديد</button></div>
      {items === null ? <div className="page-loader"><span className="loader" /></div> : items.length === 0 ? <div className="empty">لا توجد إنجازات بعد — أضف أول إنجاز</div> : (
        <div className="ach-grid">{items.map((a) => { const imgs = achievementImages(a); return (
          <div className="ach-card" key={a.id} data-testid={`admin-ach-${a.id}`}>
            {imgs[0] && <div className="ach-img"><img src={imgs[0]} alt={a.title} />{imgs.length > 1 && <span className="ach-count">{imgs.length} صور</span>}</div>}
            <div className="ach-body"><h3>{a.title}</h3>{a.description && <p style={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{a.description}</p>}
              {a.journal && <div className="ach-meta"><span className="badge badge-green">{a.journal}</span></div>}
              <div className="row-actions" style={{ marginTop: 12 }}><button className="mini-btn" onClick={() => setEditing(a)} data-testid={`edit-ach-${a.id}`}><Pencil size={14} />تعديل</button><button className="mini-btn danger" onClick={() => remove(a)} data-testid={`delete-ach-${a.id}`}><Trash2 size={14} /></button></div></div>
          </div>); })}</div>
      )}
      {editing !== undefined && <AchievementForm initial={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />}
    </div>
  );
}
